import { FormEvent, useEffect, useState, type CSSProperties } from "react";
import { CalendarDays, ShieldCheck, Truck, Wrench } from "lucide-react";
import { FunctionalApp as Dashboard } from "./FunctionalApp";
import { PlatformAdmin } from "./PlatformAdmin";
import { PublicBooking } from "./PublicBooking";

type AuthState = "checking" | "signed-out" | "signed-in";
export type SessionPayload = {
  user: {
    id: string;
    name: string;
    email: string;
    isTenantOwner: boolean;
    isStaff: boolean;
    permissions: string[];
    products: ("MAINTENANCE" | "DISPATCH" | "APPOINTMENTS")[];
    groups: {
      id: string;
      name: string;
      product: "MAINTENANCE" | "DISPATCH" | "APPOINTMENTS" | null;
    }[];
  };
  tenant: { id: string; name: string; slug: string; billingState: string };
};
type Branding = {
  companyName: string;
  logoUrl: string | null;
  primaryColor: string;
  accentColor: string;
  signInHeading: string;
  welcomeMessage: string;
};
const defaultBranding: Branding = {
  companyName: "TaskRoute",
  logoUrl: null,
  primaryColor: "#102a43",
  accentColor: "#12a594",
  signInHeading: "Sign in to your workspace",
  welcomeMessage: "Maintenance and dispatch operations, securely in one place.",
};
const apiOrigin = location.hostname.endsWith(".taskroute.co.uk")
  ? "https://taskroute.co.uk"
  : "";
const api = (path: string) => `${apiOrigin}${path}`;
const tenantHost =
  location.hostname.endsWith(".taskroute.co.uk") &&
  location.hostname !== "www.taskroute.co.uk";
const hostnameTenant = tenantHost
  ? location.hostname.slice(0, -".taskroute.co.uk".length)
  : "";

export function AuthenticatedApp() {
  const bookingPage = location.pathname === "/book";
  const publicHome =
    location.hostname === "taskroute.co.uk" && location.pathname === "/";
  const controlPage =
    location.hostname === "taskroute.co.uk" && location.pathname === "/control";
  const [auth, setAuth] = useState<AuthState>("checking");
  const [company, setCompany] = useState(() =>
    controlPage
      ? "taskroute-admin"
      : hostnameTenant ||
        localStorage.getItem("taskroute-tenant") ||
        "northstar",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [branding, setBranding] = useState<Branding>(defaultBranding);

  async function loadSession(slug: string, signal?: AbortSignal) {
    const response = await fetch(api("/api/me"), {
      credentials: "include",
      headers: { "x-tenant-slug": slug },
      signal,
    });
    if (!response.ok) {
      setSession(null);
      setAuth("signed-out");
      return false;
    }
    const next: SessionPayload = await response.json();
    localStorage.setItem("taskroute-tenant", next.tenant.slug);
    if (
      !tenantHost &&
      !publicHome &&
      location.hostname === "taskroute.co.uk" &&
      location.pathname !== "/control"
    ) {
      location.replace(`https://${next.tenant.slug}.taskroute.co.uk/`);
      return true;
    }
    setSession(next);
    setAuth("signed-in");
    return true;
  }
  useEffect(() => {
    const controller = new AbortController();
    loadSession(company, controller.signal).catch(() => setAuth("signed-out"));
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (auth === "signed-in") return;
    const controller = new AbortController(),
      slug = company.trim().toLowerCase();
    const timer = setTimeout(() => {
      if (!slug) return setBranding(defaultBranding);
      fetch(api("/api/branding"), {
        headers: { "x-tenant-slug": slug },
        signal: controller.signal,
      })
        .then(async (response) => {
          if (response.ok) setBranding(await response.json());
          else setBranding(defaultBranding);
        })
        .catch(() => setBranding(defaultBranding));
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [company, auth]);

  async function login(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const slug = (hostnameTenant || company).trim().toLowerCase();
      const response = await fetch(api("/api/auth/login"), {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", "x-tenant-slug": slug },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        setError(
          response.status === 401
            ? "The email or password is incorrect."
            : "Unable to sign in. Check the company address and try again.",
        );
        return;
      }
      localStorage.setItem("taskroute-tenant", slug);
      if (
        !tenantHost &&
        location.hostname === "taskroute.co.uk" &&
        location.pathname !== "/control"
      ) {
        location.assign(`https://${slug}.taskroute.co.uk/`);
        return;
      }
      await loadSession(slug);
    } catch {
      setError("TaskRoute could not reach the server. Try again shortly.");
    } finally {
      setSubmitting(false);
    }
  }

  if (bookingPage) return <PublicBooking />;
  if (publicHome) return <PublicHome />;
  if (auth === "checking")
    return (
      <div className="auth-shell">
        <div className="auth-loading">Loading TaskRoute…</div>
      </div>
    );
  if (auth === "signed-in" && session) {
    if (controlPage)
      return session.user.isStaff &&
        session.tenant.slug === "taskroute-admin" ? (
        <PlatformAdmin session={session} />
      ) : (
        <div className="auth-shell">
          <div className="auth-loading">
            TaskRoute Administration access required.
          </div>
        </div>
      );
    return <Dashboard session={session} />;
  }
  const brandStyle = {
    "--tenant-primary": branding.primaryColor,
    "--tenant-accent": branding.accentColor,
  } as CSSProperties;
  return (
    <div className="auth-shell tenant-branded" style={brandStyle}>
      <section className="auth-panel">
        <div className="auth-brand">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={`${branding.companyName} logo`} />
          ) : (
            <span>◆</span>
          )}
          <span>
            {controlPage ? "TaskRoute Control" : branding.companyName}
          </span>
          <small>Powered by TaskRoute</small>
        </div>
        <div className="auth-copy">
          <ShieldCheck />
          <div>
            <h1>
              {controlPage
                ? "Platform administrator sign in"
                : branding.signInHeading}
            </h1>
            <p>
              {controlPage
                ? "Secure access for authorised TaskRoute staff only."
                : branding.welcomeMessage}
            </p>
          </div>
        </div>
        <form onSubmit={login}>
          {!tenantHost && !controlPage ? (
            <label>
              Company address
              <div className="slug-field">
                <input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  autoComplete="organization"
                  required
                />
                <span>.taskroute.co.uk</span>
              </div>
            </label>
          ) : null}
          <label>
            Email address
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              autoFocus
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          {error ? (
            <p className="auth-error" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <footer>
          <Wrench />{" "}
          {controlPage
            ? "Restricted platform administration area."
            : `Need help? Contact your ${branding.companyName} administrator.`}
        </footer>
      </section>
      <aside className="auth-art">
        <div>
          <strong>
            {controlPage
              ? "Manage TaskRoute securely."
              : "Keep operations moving."}
          </strong>
          <p>
            {controlPage
              ? "Create and govern customer and sandbox workspaces from one isolated administration account."
              : "Plan maintenance, respond to breakdowns and coordinate dispatch from a single operational record."}
          </p>
        </div>
      </aside>
    </div>
  );
}

function PublicHome() {
  return (
    <div className="public-home">
      <header>
        <a className="public-logo" href="/">
          <span>◆</span>TaskRoute
        </a>
        <nav>
          <a href="#maintenance">Maintenance</a>
          <a href="#dispatch">Dispatch</a>
          <a href="#appointments">Appointments</a>
          <a href="#security">Security</a>
        </nav>
        <a className="public-login" href="/login">
          Sign in
        </a>
      </header>
      <main>
        <section className="public-hero">
          <div>
            <span className="public-kicker">
              Operations and bookings, properly connected
            </span>
            <h1>Run maintenance, dispatch and appointments with clarity.</h1>
            <p>
              TaskRoute provides three separately enabled products in one secure
              platform: Maintenance, Dispatch and Appointments.
            </p>
            <div className="public-actions">
              <a href="/login">Sign in to your company</a>
              <a href="#products">Explore TaskRoute</a>
            </div>
          </div>
          <div className="public-preview">
            <div>
              <small>LIVE OPERATIONS</small>
              <strong>
                One platform.
                <br />
                Three separate products.
              </strong>
            </div>
            <ul>
              <li>
                <Wrench />
                Maintenance and CMMS
              </li>
              <li>
                <Truck />
                Dispatch and pallet tracking
              </li>
              <li>
                <CalendarDays />
                Appointments and service calendars
              </li>
              <li>
                <ShieldCheck />
                Tenant-isolated and permission controlled
              </li>
            </ul>
          </div>
        </section>
        <section className="public-products" id="products">
          <article id="maintenance">
            <Wrench />
            <small>MAINTENANCE</small>
            <h2>Prevent downtime. Respond with clarity.</h2>
            <p>
              Manage assets, breakdowns, work orders, planned maintenance, spare
              parts, temporary repairs, sign-off and downtime reporting.
            </p>
            <ul>
              <li>Work claiming, assignments and assistants</li>
              <li>PPM schedules and reactivation catch-up</li>
              <li>Inventory, bin locations and stock history</li>
              <li>Production stopped escalation</li>
            </ul>
          </article>
          <article id="dispatch">
            <Truck />
            <small>DISPATCH</small>
            <h2>Know where every order and pallet is.</h2>
            <p>
              Coordinate picking, runners, trailers, export loads, customer
              collections, manifests and signatures without losing the
              operational trail.
            </p>
            <ul>
              <li>Individual pallet tracking</li>
              <li>Two-scan trailer loading</li>
              <li>Runner queues and wrong-load protection</li>
              <li>Signed manifests and status search</li>
            </ul>
          </article>
          <article id="appointments">
            <CalendarDays />
            <small>APPOINTMENTS</small>
            <h2>Turn availability into confirmed bookings.</h2>
            <p>
              Configure staff, services, prices, duration, cleanup time,
              deposits and customer appointments without double-booking.
            </p>
            <ul>
              <li>Staff-specific services, prices and durations</li>
              <li>Availability and conflict-safe calendars</li>
              <li>Telephone, walk-in and online booking sources</li>
              <li>Stripe and Square connected-account setup</li>
            </ul>
          </article>
        </section>
        <section className="public-security" id="security">
          <div>
            <small>BUILT FOR ACCOUNTABILITY</small>
            <h2>Customer data stays separated.</h2>
          </div>
          <p>
            Each company has its own workspace, product seats, groups and
            permissions. Support access is temporary, customer controlled and
            audited. Suspended customers retain export access without receiving
            free operational workflows.
          </p>
        </section>
      </main>
      <footer>
        <span>© {new Date().getFullYear()} TaskRoute</span>
        <a href="/login">Customer sign in</a>
      </footer>
    </div>
  );
}
